import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getAuthToken,
  getUser,
  clearLocalAuthState,
  logout as logoutRedirect,
} from '@/utils/auth';
import { clearSubscription, isAdmin as emailIsAdmin } from '@/utils/subscription';
import axios from 'axios';
import { AUTH_API } from '@/config/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

async function fetchMe(legacyToken) {
  const headers = {};
  // TODO(remove-by-2026-10-01): temporary Bearer for pre-migration sessions
  if (legacyToken) {
    headers.Authorization = `Bearer ${legacyToken}`;
  }
  const response = await axios.get(`${AUTH_API}/me`, {
    withCredentials: true,
    headers,
    timeout: 15000,
  });
  return response.data;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileCompleted, setProfileCompleted] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      // Prefer cookie session via /me; fall back to legacy localStorage JWT
      const legacyToken = getAuthToken();
      try {
        const profileData = await fetchMe(legacyToken || undefined);
        if (profileData) {
          setUser(profileData);
          setProfileCompleted(!!profileData.profile_completed);
          setIsAuthenticated(true);
          // Keep legacy token in memory only if still present (do not write new JWTs)
          if (legacyToken) setToken(legacyToken);
          return;
        }
      } catch (_) {
        // Not authenticated via cookie
      }

      if (legacyToken) {
        // TODO(remove-by-2026-10-01): legacy localStorage path
        const storedUser = getUser();
        if (storedUser) {
          setUser(storedUser);
          setToken(legacyToken);
          setIsAuthenticated(true);
          setProfileCompleted(!!storedUser.profile_completed);
        } else {
          clearLocalAuthState();
        }
      }
      setLoading(false);
    };

    initAuth().finally(() => setLoading(false));
  }, []);

  /**
   * Establish session after login/register.
   * Cookie is set by the server; do NOT write JWT to localStorage.
   */
  const login = async (userData, _authToken) => {
    try {
      // Refresh from /me so role/subscription come from the server
      try {
        const profileData = await fetchMe();
        if (profileData) {
          setUser(profileData);
          setProfileCompleted(!!profileData.profile_completed);
          setIsAuthenticated(true);
          setToken(null);
          return;
        }
      } catch (_) {
        // Fall through to response body user
      }
      setUser(userData);
      setIsAuthenticated(true);
      setProfileCompleted(!!userData?.profile_completed);
      setToken(null);
    } catch (error) {
      throw error;
    }
  };

  const logout = async (redirectTo = '/') => {
    try {
      await axios.post(`${AUTH_API}/logout`, {}, { withCredentials: true, timeout: 10000 });
    } catch (_) {
      // Still clear client state
    }
    clearSubscription();
    clearLocalAuthState();
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setProfileCompleted(false);
    try {
      if (axios?.defaults?.headers?.common) {
        delete axios.defaults.headers.common.Authorization;
      }
    } catch (_) {
      /* ignore */
    }
    const dest = typeof redirectTo === 'string' && redirectTo.startsWith('/') ? redirectTo : '/';
    window.location.href = dest;
  };

  const updateUser = (userData) => {
    setUser(userData);
    setProfileCompleted(!!userData?.profile_completed);
  };

  const refreshUser = async () => {
    const profileData = await fetchMe(getAuthToken() || undefined);
    if (profileData) {
      setUser(profileData);
      setProfileCompleted(!!profileData.profile_completed);
      setIsAuthenticated(true);
    }
    return profileData;
  };

  const value = {
    user,
    token,
    isAuthenticated,
    profileCompleted,
    loading,
    login,
    logout,
    updateUser,
    refreshUser,
    isAdmin: emailIsAdmin(user),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
